import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import Input from 'components/inputs/input/Input';
import Tooltip from 'components/Tooltip';
import ContentSlideDown from 'components/content-slide-down/ContentSlideDown';

import { __t, applyTranslations } from 'utils/language';

import './ccm-campaign-production-budget.scss';

applyTranslations(require('./ccm-campaign-production-budget.i18n.json'));

import type {
  CampaignPackageToDetails,
  CampaignPackageType
} from 'modules/campaigns/components/campaign-package/types';

type Props = {
  minProductionBudget: number | null,
  maxProductionBudget: number | null,
  packageToDetailsMap: CampaignPackageToDetails,
  campaignPackage: CampaignPackageType | undefined,
  disabled?: boolean,
  onFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void
};

const MSG_KEY = 'ccm_campaign_production_budget';

const CcmCampaignProductionBudget: React.FC<Props> = ({
  minProductionBudget,
  maxProductionBudget,
  packageToDetailsMap,
  campaignPackage,
  disabled,
  onFieldChange
}): JSX.Element => {
  const renderProductionBudgetDescription = (): React.ReactNode => {
    if (isNil(campaignPackage)) {
      return null;
    }

    const numOutfits = packageToDetailsMap[campaignPackage].numOutfits;

    return (
      <ContentSlideDown
        trigger={(
          <div className="ccm-campaign-production-budget__description">
            {__t(`${MSG_KEY}.trigger`, {
              minValue: minProductionBudget,
              maxValue: maxProductionBudget
            })}
          </div>
        )}
        withChevron
        withTriggerInSlidedown
      >
        <div className="ccm-campaign-production-budget__body">
          <div className="ccm-campaign-production-budget__item">
            <div className="ccm-campaign-production-budget__item-label">
              {__t(`${MSG_KEY}.budget_per_outfit`)}
            </div>
            <div className="ccm-campaign-production-budget__item-value">
              {minProductionBudget}€
            </div>
          </div>
          <div className="ccm-campaign-production-budget__item">
            <div className="ccm-campaign-production-budget__item-label">
              {__t(`${MSG_KEY}.num_outfits`)}
            </div>
            <div className="ccm-campaign-production-budget__item-value">
              {numOutfits}
            </div>
          </div>
        </div>
      </ContentSlideDown>
    );
  };

  const renderProductionBudgetInput = (): React.ReactNode => {
    const BudgetInput = (
      <Input
        hint="€"
        hintAlign="right"
        name="contentProductionBudget"
        value={!isNil(minProductionBudget) ? String(minProductionBudget) : ''}
        placeholder="100"
        width="responsive"
        maxLength={4}
        isEditable={!isEmpty(campaignPackage)}
        isNotEditable={isEmpty(campaignPackage) || disabled}
        data={{ identifier: 'contentProductionBudget' }}
        onChange={onFieldChange}
      />
    );

    if (isEmpty(campaignPackage) || disabled) {
      const msgKey = isEmpty(campaignPackage)
        ? 'campaign_package'
        : 'production_budget_disabled';

      return (
        <div className="ccm-campaign-production-budget__input">
          <Tooltip
            message={__t(`${MSG_KEY}.tooltips.${msgKey}`)}
          >
            {BudgetInput}
          </Tooltip>
        </div>
      );
    }

    return (
      <div className="ccm-campaign-production-budget__input">
        {BudgetInput}
      </div>
    );
  };

  return (
    <div className="ccm-campaign-production-budget">
      {renderProductionBudgetInput()}
      {(minProductionBudget ?? 0) > 0 && renderProductionBudgetDescription()}
    </div>
  );
};

export default CcmCampaignProductionBudget;
